import * as React from 'react';
import styled from 'styled-components';
import {TextButton} from '../../Button';
import {
  BOXSHADOWS,
  COLORS,
  ComponentLStyling,
  ComponentSStyling,
  ComponentTextStyle,
  ComponentXSStyling,
  Z_INDEXES
} from '../../styles';
import {Size} from '../../types';
import {flowDown, UserMenuSectionListStyling} from '../mobile/CommonStyles';
import {SystemIcons} from '../../icons';
import {HyperLink} from '../../HyperLink';
import {MenuNavigationItem, MenuNavigationSection, ProfileMenu} from '../types';
import Breakpoints from '../../styles/breakpoints';
import {MenuItem} from '../../MenuItem';
import {useNavigation} from '../../Navigation/NavigationProvider';
import {ProfileButton} from '../../ProfileButton';

const UserMenuScrollContainer = styled.div`
  flex: 1;
`;

const UserMenuPinnedContainer = styled.div``;


const UserMenuWrapper = styled.div`
  margin: 0;
  background-color: ${props => COLORS.getColor('white', props.theme)};
  box-sizing: border-box;
  display: none;
  width: 320px;

  position: absolute;

  &.show {
    display: block;
  }

  top: 0;
  right: 0;
  z-index: ${Z_INDEXES.off_canvas};
  height: 100%;
  flex-flow: column;


  ${Breakpoints.MEDIUM} {
    flex-flow: initial;
    top: initial;
    right: initial;
    height: initial;
    animation: ${flowDown} 0.2s ease-in-out;
    box-shadow: ${BOXSHADOWS.BOXSHADOW_CENTERED};
    border-radius: 8px;
    border: 1px solid ${props => COLORS.getColor('neutral_200', props.theme)};
    z-index: ${Z_INDEXES.fixed_menu};
    padding: 24px 0;
  }
`;


const MenuSection = styled.div`
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  width: 100%;

  &.divider {
    border-top: 1px solid ${props => COLORS.getColor('neutral_200', props.theme)};
  }
`;

const SignOutActionWrapper = styled(MenuSection)`
  align-items: center;
  justify-content: center;
`;

const MenuSectionHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 24px 4px 24px;
  box-sizing: border-box;
  min-height: 32px;
  background-color: ${props => COLORS.getColor('neutral_20', props.theme)};

  ${props => ComponentSStyling(ComponentTextStyle.Bold, COLORS.getColor('neutral_500', props.theme))}
`;

const MenuSectionList = styled.div`
  margin: 0;
  padding: 0;

  ${UserMenuSectionListStyling};
`;


const UserSectionAvatar = styled.div`
  pointer-events: none;

  button {
    padding: 0 !important;
  }

  button > div {
    width: 48px !important;
    height: 48px !important;
    border-radius: 50% !important;
  }
`;

const UserSectionInfo = styled.div`
  padding: 2px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserSectionInfoName = styled.div`
  ${props => ComponentLStyling(ComponentTextStyle.Bold, COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme))}
`;

const UserSectionInfoEmail = styled.div`
  word-break: break-all;
  ${props => ComponentXSStyling(ComponentTextStyle.Regular, COLORS.getColor('neutral_500', props.theme))}
`;

const UserSectionInfoLink = styled.div`
  padding: 2px 0;
  display: flex;
  align-items: center;
  gap: 4px;

  ${ComponentXSStyling(ComponentTextStyle.Bold, null)}
  svg {
    width: 16px;
    height: 16px;
  }
`;

const UserSection = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0 24px 8px 24px;
  gap: 16px;
`;

interface UserMenuProps extends ProfileMenu {
  show?: boolean;
  onHideUserMenu: () => void;
}

const UserMenu = ({show, user, onHideUserMenu, sections, signOut, label}: UserMenuProps) => {

  const {navigate, isActiveRoute} = useNavigation();

  React.useEffect(() => {
    if (!onHideUserMenu) return;

    function handleKeyPress(e: any) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        onHideUserMenu();
      }
    }

    document?.addEventListener('keydown', handleKeyPress, false);
    return () => {
      document?.removeEventListener('keydown', handleKeyPress, false);
    };
  }, [onHideUserMenu]);

  const renderItem = (item: MenuNavigationItem, section: MenuNavigationSection, index: number) =>
    <MenuItem key={index}
              id={`profile_${index}`}
              active={!item.external && !!isActiveRoute && isActiveRoute(item.to, !!item.exact)}
              item={{
                value: item.to,
                disabled: item.disabled,
                icon: item.icon,
                displayLabel: item.label,
              }}
              onClickHandler={(e) => {
                item.action && item.action(e);
                navigate && navigate(item.to, !!item.external);
              }}/>

  return (
    <UserMenuWrapper onClick={onHideUserMenu}
                     className={show ? 'show' : ''}
                     role="menu"
                     aria-labelledby="UserMenuButton">
      <UserMenuScrollContainer>
        <UserSection>
          <UserSectionAvatar>
            <ProfileButton onClick={() => {
            }}
                           tabIndex={-1}
                           initials={user.firstName[0] + user.lastName[0]}/>
          </UserSectionAvatar>
          <UserSectionInfo>
            <UserSectionInfoName data-hj-suppress>{`${user.firstName} ${user.lastName}`}</UserSectionInfoName>
            <UserSectionInfoEmail data-hj-suppress>{user.email}</UserSectionInfoEmail>
            {
              user.link &&
              <HyperLink variant={'styleless'}
                         href={user.link.href}
                         target={user.link.target}
                         id={user.link.id}
                         onClick={user.link.onClick}>
                <UserSectionInfoLink>
                  <span>
                    {user.link.label}
                  </span>
                  {user.link.icon && React.cloneElement(user.link.icon, {size: '16px'})}
                </UserSectionInfoLink>
              </HyperLink>
            }
          </UserSectionInfo>
        </UserSection>
        {
          sections.filter(a => !a.pinned).map((section, index) =>
            <MenuSection key={index} className={section.divider ? 'divider' : ''}>
              {section.label && <MenuSectionHeader>{section.label}</MenuSectionHeader>}
              <MenuSectionList key={index}>
                {section.items.map((item, index) => renderItem(item, section, index))}
              </MenuSectionList>
            </MenuSection>
          )
        }
      </UserMenuScrollContainer>
      <UserMenuPinnedContainer>
        {
          sections.filter(a => !!a.pinned).map((section, index) =>
            <MenuSection key={index} className={section.divider || index === 0 ? 'divider' : ''}>
              {section.label && <MenuSectionHeader>{section.label}</MenuSectionHeader>}
              <MenuSectionList key={index}>
                {section.items.map((item, index) => renderItem(item, section, index))}
              </MenuSectionList>
            </MenuSection>
          )
        }
        {
          signOut &&
          <SignOutActionWrapper>
            <TextButton width={'272px'}
                    variant="secondary"
                    icon={<SystemIcons.Logout/>}
                    size={Size.Large}
                    onClick={e => {
                      e.preventDefault();
                      signOut?.action();
                    }}>
              {signOut?.label}
            </TextButton>
          </SignOutActionWrapper>
        }
      </UserMenuPinnedContainer>
    </UserMenuWrapper>
  );
};

export default UserMenu;
